// src/shared/components/telemetry/CreditBadge.tsx
// ─────────────────────────────────────────────────────────────────────────────
// CreditBadge — Compact neon-coded credit indicator for nav bar.
//
// Consumes useCreditBadge() from the telemetry pipeline.
// Renders a tiny pill with credit level color + percentage.
// Automatically reflects real-time metering state.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { useCreditBadge, type CreditLevel } from '@/features/usage/hooks/useUsage';

const LEVEL_STYLES: Record<CreditLevel, string> = {
  ok:       'bg-emerald-900/40 text-emerald-400 border-emerald-800/30',
  warning:  'bg-amber-900/40   text-amber-400   border-amber-800/30',
  critical: 'bg-red-900/40     text-red-400     border-red-800/30',
  exceeded: 'bg-red-900/60     text-red-300     border-red-700/50 animate-pulse',
};

const LEVEL_DOT: Record<CreditLevel, string> = {
  ok:       'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]',
  warning:  'bg-amber-400   shadow-[0_0_6px_rgba(251,191,36,0.5)]',
  critical: 'bg-red-400     shadow-[0_0_6px_rgba(248,113,113,0.5)]',
  exceeded: 'bg-red-300     shadow-[0_0_8px_rgba(252,165,165,0.6)]',
};

export interface CreditBadgeProps {
  /** Show the percentage number (default: true) */
  showPct?: boolean;
  /** Extra classes on the outer container */
  className?: string;
}

export default function CreditBadge({
  showPct = true,
  className = '',
}: CreditBadgeProps) {
  const { level, todayPctHardLimit, isMeteringActive, isLoading } = useCreditBadge();

  // Don't render if metering is disabled or still loading
  if (!isMeteringActive || isLoading) return null;

  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        px-2 py-0.5
        rounded-full
        border
        text-[10px] font-medium tabular-nums
        ${LEVEL_STYLES[level]}
        ${className}
      `}
      title={`Credits: ${Math.round(todayPctHardLimit)}% of daily limit used`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${LEVEL_DOT[level]}`} />
      {showPct && <span>{Math.round(todayPctHardLimit)}%</span>}
    </span>
  );
}
